/**
 * Clause-Level Hierarchical ID System Utilities (Frontend)
 *
 * ID Structure:
 * - ClauseID: CL-{contractId}-{clauseId} or CL-{contractId}-{counterOfferId}-{clauseId}
 *
 * Enhanced with properties for visibility, actionability, and display logic
 */

export interface ClauseProperties {
  // Core identification
  hierarchicalId: string;
  clauseId: string;
  contractId: string;
  counterOfferId?: string;

  // Negotiation state
  status:
    | 'original'
    | 'proposed'
    | 'countered'
    | 'accepted'
    | 'rejected'
    | 'ratified';
  proposedBy: 'buyer' | 'seller' | 'agent';
  lastModifiedBy: 'buyer' | 'seller' | 'agent';
  lastModifiedAt: Date;

  // Visibility rules
  visibleTo: ('buyer' | 'seller' | 'agent')[];
  hiddenFrom: ('buyer' | 'seller' | 'agent')[];

  // Actionability rules
  canEdit: ('buyer' | 'seller' | 'agent')[];
  canCounter: ('buyer' | 'seller' | 'agent')[];
  canAccept: ('buyer' | 'seller' | 'agent')[];
  canReject: ('buyer' | 'seller' | 'agent')[];

  // Display logic
  displayInNegotiation: boolean;
  displayInContract: boolean;
  displayInCounterOffer: boolean;
  requiresAttention: boolean;
  isUrgent: boolean;

  // Contract state dependencies
  requiresRatification: boolean;
  isAddendum: boolean;
  isExtension: boolean;
  isRepairRequest: boolean;
}

/**
 * Generate a Clause ID from a Contract ID and clause identifier
 * @param contractId - The parent Contract ID
 * @param clauseId - The clause identifier (e.g., 'offer-price', 'closing-date')
 * @param counterOfferId - Optional counter offer ID for counter-offer clauses
 * @returns Clause ID (e.g., CL-abc123-xyz789-def456-offer-price or CL-abc123-xyz789-def456-CO-ghi789-offer-price)
 */
export function generateClauseId(
  contractId: string,
  clauseId: string,
  counterOfferId?: string
): string {
  if (!contractId) {
    throw new Error('Invalid Contract ID provided');
  }

  // Handle both hierarchical IDs (C-xxx) and regular ObjectIds
  let normalizedContractId = contractId;
  if (!contractId.startsWith('C-')) {
    // Convert ObjectId to hierarchical format
    normalizedContractId = `C-${contractId}`;
  }

  if (!clauseId || typeof clauseId !== 'string') {
    throw new Error('Invalid clause ID provided');
  }

  // Validate clause ID format (should be kebab-case)
  if (!/^[a-z][a-z0-9-]*[a-z0-9]$/.test(clauseId)) {
    throw new Error(
      'Clause ID must be in kebab-case format (e.g., offer-price, closing-date)'
    );
  }

  if (counterOfferId) {
    if (!counterOfferId.startsWith('CO-')) {
      throw new Error('Invalid Counter Offer ID provided');
    }
    return `${normalizedContractId}-CO-${
      counterOfferId.split('-').slice(-1)[0]
    }-CL-${clauseId}`;
  }

  return `${normalizedContractId}-CL-${clauseId}`;
}

/**
 * Validate if a clause ID follows the correct format
 * @param id - The ID to validate
 * @returns True if valid
 */
export function validateClauseId(id: string): boolean {
  if (!id || typeof id !== 'string') return false;

  const pattern = /^P-\d+-C-\d+(-CO-\d+)?-CL-[a-z-]+$/;
  return pattern.test(id);
}

/**
 * Extract clause information from a clause ID
 * @param clauseId - The clause ID
 * @returns Object containing contract ID, counter offer ID, and clause name
 */
export function extractClauseInfo(clauseId: string) {
  if (!validateClauseId(clauseId)) {
    throw new Error('Invalid clause ID format');
  }

  const parts = clauseId.split('-');
  const contractId = `${parts[0]}-${parts[1]}-${parts[2]}-${parts[3]}`;

  // Check if it's a counter-offer clause
  if (parts.length > 6 && parts[4] === 'CO') {
    return {
      contractId,
      counterOfferId: `${contractId}-CO-${parts[5]}`,
      clauseName: parts.slice(7).join('-'),
      isCounterOffer: true,
    };
  }

  return {
    contractId,
    counterOfferId: null,
    clauseName: parts.slice(5).join('-'),
    isCounterOffer: false,
  };
}

/**
 * Get all clause IDs for a contract
 * @param contractId - The Contract ID
 * @param clauseNames - Array of clause names
 * @returns Array of clause IDs
 */
export function getContractClauseIds(
  contractId: string,
  clauseNames: string[]
): string[] {
  if (!contractId || !contractId.startsWith('C-')) {
    throw new Error('Invalid Contract ID provided');
  }

  return clauseNames.map((clauseName) =>
    generateClauseId(contractId, clauseName)
  );
}

/**
 * Check if a clause ID is a counter-offer clause
 * @param clauseId - The clause ID to check
 * @returns True if it's a counter-offer clause
 */
export function isCounterOfferClause(clauseId: string): boolean {
  if (!validateClauseId(clauseId)) return false;

  const parts = clauseId.split('-');
  return parts.length > 6 && parts[4] === 'CO';
}

/**
 * Get the base clause ID (without counter-offer part)
 * @param clauseId - The clause ID
 * @returns Base clause ID without counter-offer information
 */
export function getBaseClauseId(clauseId: string): string {
  const info = extractClauseInfo(clauseId);
  return generateClauseId(info.contractId, info.clauseName);
}

/**
 * Generate counter-offer clause ID
 * @param baseClauseId - The base clause ID
 * @param counterOfferId - The counter offer ID
 * @returns Counter-offer clause ID
 */
export function generateCounterOfferClauseId(
  baseClauseId: string,
  counterOfferId: string
): string {
  const info = extractClauseInfo(baseClauseId);
  return generateClauseId(info.contractId, info.clauseName, counterOfferId);
}

/**
 * Generate clause ID with full properties
 * @param contractId - The parent Contract ID
 * @param clauseId - The clause identifier
 * @param properties - Partial clause properties
 * @param counterOfferId - Optional counter offer ID
 * @returns Complete clause properties object
 */
export function generateClauseIdWithProperties(
  contractId: string,
  clauseId: string,
  properties: Partial<ClauseProperties> = {},
  counterOfferId?: string
): ClauseProperties {
  const hierarchicalId = generateClauseId(contractId, clauseId, counterOfferId);

  // Normalize contract ID for consistency
  const normalizedContractId = contractId.startsWith('C-')
    ? contractId
    : `C-${contractId}`;

  return {
    hierarchicalId,
    clauseId,
    contractId: normalizedContractId,
    counterOfferId,
    status: properties.status || 'original',
    proposedBy: properties.proposedBy || 'buyer',
    lastModifiedBy: properties.lastModifiedBy || 'buyer',
    lastModifiedAt: new Date(),
    visibleTo: properties.visibleTo || ['buyer', 'seller', 'agent'],
    hiddenFrom: properties.hiddenFrom || [],
    canEdit: properties.canEdit || [],
    canCounter: properties.canCounter || ['seller', 'agent'],
    canAccept: properties.canAccept || ['buyer'],
    canReject: properties.canReject || ['buyer'],
    displayInNegotiation: properties.displayInNegotiation ?? true,
    displayInContract: properties.displayInContract ?? true,
    displayInCounterOffer: properties.displayInCounterOffer ?? false,
    requiresAttention: properties.requiresAttention ?? false,
    isUrgent: properties.isUrgent ?? false,
    requiresRatification: properties.requiresRatification ?? false,
    isAddendum: properties.isAddendum ?? false,
    isExtension: properties.isExtension ?? false,
    isRepairRequest: properties.isRepairRequest ?? false,
  };
}

/**
 * Determine if clause should be shown in negotiation UI
 * @param clause - The clause properties
 * @param userRole - The user's role
 * @param contractRatified - Whether the contract is ratified
 * @returns True if clause should be shown
 */
export function shouldShowInNegotiation(
  clause: ClauseProperties,
  userRole: 'buyer' | 'seller' | 'agent',
  contractRatified: boolean
): boolean {
  // Don't show if contract is ratified and clause is not special
  if (
    contractRatified &&
    !clause.isAddendum &&
    !clause.isExtension &&
    !clause.isRepairRequest
  ) {
    return false;
  }

  // Don't show if user is not in visibleTo list
  if (!clause.visibleTo.includes(userRole)) {
    return false;
  }

  // Don't show if clause is hidden from user
  if (clause.hiddenFrom.includes(userRole)) {
    return false;
  }

  // Show if clause requires attention or is actionable by user
  return (
    clause.requiresAttention ||
    clause.canEdit.includes(userRole) ||
    clause.canCounter.includes(userRole) ||
    clause.canAccept.includes(userRole) ||
    clause.canReject.includes(userRole)
  );
}

/**
 * Determine if clause can be acted upon by user
 * @param clause - The clause properties
 * @param userRole - The user's role
 * @param action - The action to perform
 * @returns True if user can perform the action
 */
export function canUserActOnClause(
  clause: ClauseProperties,
  userRole: 'buyer' | 'seller' | 'agent',
  action: 'edit' | 'counter' | 'accept' | 'reject'
): boolean {
  const actionMap = {
    edit: clause.canEdit,
    counter: clause.canCounter,
    accept: clause.canAccept,
    reject: clause.canReject,
  };

  return actionMap[action].includes(userRole);
}

/**
 * Get default properties for clause status
 * @param status - The clause status
 * @returns Default properties for the status
 */
export function getDefaultPropertiesForStatus(
  status: ClauseProperties['status']
): Partial<ClauseProperties> {
  const defaults = {
    original: {
      visibleTo: ['buyer', 'seller', 'agent'],
      canCounter: ['seller', 'agent'],
      canAccept: [],
      canReject: [],
      displayInNegotiation: true,
      displayInContract: true,
      requiresAttention: false,
    },
    proposed: {
      visibleTo: ['buyer', 'seller', 'agent'],
      canCounter: [],
      canAccept: ['buyer'],
      canReject: ['buyer'],
      displayInNegotiation: true,
      displayInContract: true,
      requiresAttention: true,
    },
    countered: {
      visibleTo: ['buyer', 'seller', 'agent'],
      canCounter: [],
      canAccept: ['buyer'],
      canReject: ['buyer'],
      displayInNegotiation: true,
      displayInContract: true,
      requiresAttention: true,
    },
    accepted: {
      visibleTo: ['buyer', 'seller', 'agent'],
      canCounter: [],
      canAccept: [],
      canReject: [],
      displayInNegotiation: false,
      displayInContract: true,
      requiresAttention: false,
    },
    rejected: {
      visibleTo: ['buyer', 'seller', 'agent'],
      canCounter: ['seller', 'agent'],
      canAccept: [],
      canReject: [],
      displayInNegotiation: true,
      displayInContract: true,
      requiresAttention: true,
    },
    ratified: {
      visibleTo: ['buyer', 'seller', 'agent'],
      canCounter: [],
      canAccept: [],
      canReject: [],
      displayInNegotiation: false,
      displayInContract: true,
      requiresAttention: false,
    },
  };

  return defaults[status] || defaults.original;
}
